import { calculateDistance } from './geoCalculations';

/**
 * Calculate destination point given a starting point, distance and bearing
 * @param {Object} point - Start point {lat, lng}
 * @param {number} distance - Distance in meters
 * @param {number} bearing - Bearing in radians
 * @param {number} R - Earth radius in meters
 * @returns {Object} Destination point {lat, lng}
 */
function destinationPoint(point, distance, bearing, R) {
  const lat1 = point.lat * Math.PI / 180;
  const lng1 = point.lng * Math.PI / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(distance / R) +
    Math.cos(lat1) * Math.sin(distance / R) * Math.cos(bearing)
  );

  const lng2 = lng1 + Math.atan2(
    Math.sin(bearing) * Math.sin(distance / R) * Math.cos(lat1),
    Math.cos(distance / R) - Math.sin(lat1) * Math.sin(lat2)
  );

  return {
    lat: lat2 * 180 / Math.PI,
    lng: lng2 * 180 / Math.PI
  };
}

/**
 * Calculate the four corners of a rectangular crop area from 2 points and a width
 * @param {Object} point1 - Start point {lat, lng}
 * @param {Object} point2 - End point {lat, lng}
 * @param {number} width - Width in meters to expand orthogonally to the right
 * @returns {Object} Crop area with corners, width, length, rotation, and area
 */
function calculateCropAreaFromPoints(point1, point2, width) {
  if (!point1 || !point2 || !width || width <= 0) return null;

  // Calculate length (distance between point1 and point2)
  const length = calculateDistance(point1, point2);

  // Calculate bearing from point1 to point2
  const bearing = Math.atan2(
    Math.sin((point2.lng - point1.lng) * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180),
    Math.cos(point1.lat * Math.PI / 180) * Math.sin(point2.lat * Math.PI / 180) -
    Math.sin(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
    Math.cos((point2.lng - point1.lng) * Math.PI / 180)
  );

  // Perpendicular bearing (90 degrees to the right)
  const perpBearing = bearing + Math.PI / 2;

  // Earth's radius in meters
  const R = 6371000;

  // Calculate corner3 (from point2, perpendicular at distance width)
  const corner3 = destinationPoint(point2, width, perpBearing, R);

  // Calculate corner4 (from point1, perpendicular at distance width)
  const corner4 = destinationPoint(point1, width, perpBearing, R);

  // The corners now form a true rectangle with perpendicular sides
  const corner1 = point1; // Start point
  const corner2 = point2; // End point

  // Calculate rotation angle (in degrees from north)
  const rotation = calculateRotation(point1, point2);

  // Calculate area (width * length in m²)
  const area = width * length;

  return {
    corners: [corner1, corner2, corner3, corner4],
    width,
    length,
    rotation,
    area,
    center: {
      lat: (corner1.lat + corner2.lat + corner3.lat + corner4.lat) / 4,
      lng: (corner1.lng + corner2.lng + corner3.lng + corner4.lng) / 4
    }
  };
}

/**
 * Calculate rotation angle in degrees from north
 * @param {Object} point1 - Start point {lat, lng}
 * @param {Object} point2 - End point {lat, lng}
 * @returns {number} Rotation angle in degrees (0-360, where 0 is north)
 */
function calculateRotation(point1, point2) {
  const dLng = point2.lng - point1.lng;
  const dLat = point2.lat - point1.lat;

  // Calculate bearing (angle from north)
  const bearing = Math.atan2(dLng, dLat) * (180 / Math.PI);

  // Normalize to 0-360
  return (bearing + 360) % 360;
}

/**
 * Recalculate crop area corners when editing
 * @param {Object} cropArea - Existing crop area with width, length, rotation, and center
 * @returns {Array} Array of four corner points
 */
function recalculateCropAreaCorners(cropArea) {
  const { center, width, length, rotation } = cropArea;

  // Convert rotation to radians
  const rotRad = rotation * (Math.PI / 180);

  // Calculate half dimensions in degrees (approximate conversion)
  const halfWidth = (width / 2) / 111320; // meters to degrees
  const halfLength = (length / 2) / 111320;

  // Calculate corners relative to center with rotation
  const cosR = Math.cos(rotRad);
  const sinR = Math.sin(rotRad);

  const corners = [
    // Corner 1 (bottom-left relative to rotation)
    {
      lat: center.lat - halfLength * cosR + halfWidth * sinR,
      lng: center.lng - halfLength * sinR - halfWidth * cosR
    },
    // Corner 2 (bottom-right)
    {
      lat: center.lat - halfLength * cosR - halfWidth * sinR,
      lng: center.lng - halfLength * sinR + halfWidth * cosR
    },
    // Corner 3 (top-right)
    {
      lat: center.lat + halfLength * cosR - halfWidth * sinR,
      lng: center.lng + halfLength * sinR + halfWidth * cosR
    },
    // Corner 4 (top-left)
    {
      lat: center.lat + halfLength * cosR + halfWidth * sinR,
      lng: center.lng + halfLength * sinR - halfWidth * cosR
    }
  ];

  return corners;
}

/**
 * Clone a crop area by creating a new one offset perpendicular to its orientation
 * @param {Object} cropArea - Original crop area to clone
 * @param {number} separationWidth - Distance to separate the cloned crop area
 * @returns {Object} New crop area data with updated corners, center, and points
 */
function cloneCropArea(cropArea, separationWidth) {
  if (!cropArea || !cropArea.points || cropArea.points.length !== 2) {
    return null;
  }

  const { points, width } = cropArea;
  const point1 = points[0];
  const point2 = points[1];

  // Calculate bearing from point1 to point2
  const bearing = Math.atan2(
    Math.sin((point2.lng - point1.lng) * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180),
    Math.cos(point1.lat * Math.PI / 180) * Math.sin(point2.lat * Math.PI / 180) -
    Math.sin(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
    Math.cos((point2.lng - point1.lng) * Math.PI / 180)
  );

  // Perpendicular bearing (90 degrees to the right)
  const perpBearing = bearing + Math.PI / 2;

  // Total offset distance = crop area width + separation width
  const offsetDistance = width + separationWidth;

  // Earth's radius in meters
  const R = 6371000;

  // Calculate new points by offsetting both points perpendicular to the line
  const newPoint1 = destinationPoint(point1, offsetDistance, perpBearing, R);
  const newPoint2 = destinationPoint(point2, offsetDistance, perpBearing, R);

  // Calculate the new crop area with the same width
  const newCropArea = calculateCropAreaFromPoints(newPoint1, newPoint2, width);

  if (!newCropArea) {
    return null;
  }

  return {
    ...newCropArea,
    points: [newPoint1, newPoint2]
  };
}

/**
 * Clone a crop area in the longitudinal direction (along the length)
 * @param {Object} cropArea - Original crop area to clone
 * @param {number} separationWidth - Distance to separate the cloned crop area
 * @returns {Object} New crop area data with updated corners, center, and points
 */
function cloneCropAreaLongitudinal(cropArea, separationWidth) {
  if (!cropArea || !cropArea.points || cropArea.points.length !== 2) {
    return null;
  }

  const { points, width, length } = cropArea;
  const point1 = points[0];
  const point2 = points[1];

  // Calculate bearing from point1 to point2 (this is the longitudinal direction)
  const bearing = Math.atan2(
    Math.sin((point2.lng - point1.lng) * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180),
    Math.cos(point1.lat * Math.PI / 180) * Math.sin(point2.lat * Math.PI / 180) -
    Math.sin(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
    Math.cos((point2.lng - point1.lng) * Math.PI / 180)
  );

  // Total offset distance = crop area length + separation width
  const offsetDistance = length + separationWidth;

  // Earth's radius in meters
  const R = 6371000;

  // Calculate new points by offsetting both points along the longitudinal direction
  const newPoint1 = destinationPoint(point1, offsetDistance, bearing, R);
  const newPoint2 = destinationPoint(point2, offsetDistance, bearing, R);

  // Calculate the new crop area with the same width
  const newCropArea = calculateCropAreaFromPoints(newPoint1, newPoint2, width);

  if (!newCropArea) {
    return null;
  }

  return {
    ...newCropArea,
    points: [newPoint1, newPoint2]
  };
}

/**
 * Clone a crop area in both directions (perpendicular and longitudinal)
 * @param {Object} cropArea - Original crop area to clone
 * @param {number} separationWidthPerpendicular - Distance to separate in perpendicular direction
 * @param {number} separationWidthLongitudinal - Distance to separate in longitudinal direction
 * @returns {Object} New crop area data with updated corners, center, and points
 */
function cloneCropAreaBidirectional(cropArea, separationWidthPerpendicular, separationWidthLongitudinal) {
  if (!cropArea || !cropArea.points || cropArea.points.length !== 2) {
    return null;
  }

  const { points, width, length } = cropArea;
  const point1 = points[0];
  const point2 = points[1];

  // Calculate bearing from point1 to point2
  const bearing = Math.atan2(
    Math.sin((point2.lng - point1.lng) * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180),
    Math.cos(point1.lat * Math.PI / 180) * Math.sin(point2.lat * Math.PI / 180) -
    Math.sin(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
    Math.cos((point2.lng - point1.lng) * Math.PI / 180)
  );

  // Perpendicular bearing (90 degrees to the right)
  const perpBearing = bearing + Math.PI / 2;

  // Earth's radius in meters
  const R = 6371000;

  // First offset perpendicular
  const perpOffsetDistance = width + separationWidthPerpendicular;
  const tempPoint1 = destinationPoint(point1, perpOffsetDistance, perpBearing, R);
  const tempPoint2 = destinationPoint(point2, perpOffsetDistance, perpBearing, R);

  // Then offset longitudinal
  const longOffsetDistance = length + separationWidthLongitudinal;
  const newPoint1 = destinationPoint(tempPoint1, longOffsetDistance, bearing, R);
  const newPoint2 = destinationPoint(tempPoint2, longOffsetDistance, bearing, R);

  // Calculate the new crop area with the same width
  const newCropArea = calculateCropAreaFromPoints(newPoint1, newPoint2, width);

  if (!newCropArea) {
    return null;
  }

  return {
    ...newCropArea,
    points: [newPoint1, newPoint2]
  };
}

export { calculateCropAreaFromPoints, calculateRotation, recalculateCropAreaCorners, cloneCropArea, cloneCropAreaLongitudinal, cloneCropAreaBidirectional };
